// The run's pending proposals, item by item (spec 2026-10-24-02): what each
// is about, a contract value change's current and proposed value side by
// side, and approve / reject on each for data.write.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import type { AgentRun, AgentRunItemDecisionRequest, AgentRunProposalItem } from '../../api/schema'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/skeleton'
import { decideAgentRunItem, isActiveRun, useAgentRunProposals, useInvalidateAgentRuns } from './agent-runs-api'

type EntityType = AgentRunItemDecisionRequest['entity_type']

function formatValue(v: unknown): string {
    if (v === null || v === undefined) return '—'
    if (typeof v === 'string') return v
    return JSON.stringify(v)
}

function SubjectLink({ org, item }: { org: string; item: AgentRunProposalItem }) {
    const label = item.label || '—'
    const uid = item.subject_uid
    if (!uid) return <span>{label}</span>
    const cls = 'font-medium text-primary hover:underline'
    switch (item.subject_type) {
        case 'asset':
            return <Link to="/$org/assets/$assetUid" params={{ org, assetUid: uid }} className={cls}>{label}</Link>
        case 'contract':
            return <Link to="/$org/contracts/$contractUid" params={{ org, contractUid: uid }} className={cls}>{label}</Link>
        case 'document':
            return <Link to="/$org/documents/$docUid" params={{ org, docUid: uid }} className={cls}>{label}</Link>
        case 'intervention':
            return <Link to="/$org/interventions/$interventionUid" params={{ org, interventionUid: uid }} className={cls}>{label}</Link>
        default:
            return <span className="font-medium text-foreground">{label}</span>
    }
}

export function AgentRunProposals({ org, run, canDecide }: { org: string; run: AgentRun; canDecide: boolean }) {
    const { t } = useTranslation('agent-runs')
    const { data, isLoading, refetch } = useAgentRunProposals(org, run.uid, run.pending_proposals.total > 0)
    const invalidate = useInvalidateAgentRuns(org)
    const [busy, setBusy] = useState<string | undefined>()
    const [notes, setNotes] = useState<Record<string, string>>({})
    const decidable = canDecide && !isActiveRun(run)

    if (run.pending_proposals.total === 0 && Object.keys(notes).length === 0) return null

    const decide = async (item: AgentRunProposalItem, decision: 'approve' | 'reject') => {
        setBusy(item.uid)
        try {
            const { result, error } = await decideAgentRunItem(org, run.uid, {
                entity_type: item.entity_type as EntityType,
                uid: item.uid,
                decision,
            })
            const left = result?.left.find((l) => l.uid === item.uid)
            if (error) setNotes((n) => ({ ...n, [item.uid]: error.title || t('page.proposals.decision_failed') }))
            else if (left) setNotes((n) => ({ ...n, [item.uid]: t(`page.proposals.${left.code}`, { defaultValue: left.code }) }))
            await refetch()
            await invalidate()
        } finally {
            setBusy(undefined)
        }
    }

    const items = data?.items ?? []

    return (
        <section className="rounded-lg border border-border bg-card p-4" data-testid="agent-run-proposals">
            <h2 className="mb-2 text-base font-semibold text-foreground">{t('page.proposals.title')}</h2>
            {isLoading ? (
                <Skeleton className="h-20 w-full" />
            ) : items.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="agent-run-proposals-empty">{t('page.proposals.empty')}</p>
            ) : (
                <ul className="divide-y divide-border">
                    {items.map((item) => (
                        <li
                            key={item.uid}
                            className="flex flex-col gap-2 py-2 text-sm sm:flex-row sm:items-start sm:justify-between"
                            data-testid={`agent-run-proposal-${item.uid}`}
                        >
                            <div className="min-w-0 space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge variant="warning" data-testid="agent-run-proposal-type">
                                        {t(`page.proposals.types.${item.entity_type}`, { defaultValue: item.entity_type })}
                                    </Badge>
                                    <SubjectLink org={org} item={item} />
                                    {item.detail && <span className="text-muted-foreground">· {item.detail}</span>}
                                </div>
                                {item.before !== undefined && (
                                    <div className="grid grid-cols-[6rem_1fr] gap-x-2 text-xs" data-testid="agent-run-proposal-values">
                                        <span className="text-muted-foreground">{t('page.proposals.before')}</span>
                                        <span className="break-words line-through" data-testid="agent-run-proposal-before">{formatValue(item.before)}</span>
                                        <span className="text-muted-foreground">{t('page.proposals.after')}</span>
                                        <span className="break-words font-medium" data-testid="agent-run-proposal-after">{formatValue(item.after)}</span>
                                    </div>
                                )}
                                {item.before === undefined && item.after !== undefined && (
                                    <div className="break-words text-xs text-foreground" data-testid="agent-run-proposal-after">{formatValue(item.after)}</div>
                                )}
                                {notes[item.uid] && (
                                    <div className="text-xs text-destructive" data-testid="agent-run-proposal-note">{notes[item.uid]}</div>
                                )}
                            </div>
                            {decidable && (
                                <div className="flex shrink-0 gap-2">
                                    <Button
                                        size="sm"
                                        onClick={() => void decide(item, 'approve')}
                                        disabled={busy !== undefined}
                                        data-testid="agent-run-proposal-approve"
                                    >
                                        {t('page.proposals.approve')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => void decide(item, 'reject')}
                                        disabled={busy !== undefined}
                                        data-testid="agent-run-proposal-reject"
                                    >
                                        {t('page.proposals.reject')}
                                    </Button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}
